import { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { useGetPrintJobExecutorsAllPagesQuery } from '../../../../store/api/print-job-executors-api'
import { useUser } from '../../../../hooks/use-user'
import PrintJobExecutor, { Reasons } from '../../../../types/api/print-job-executor'
import { EndpointQueryParameters } from '../../../../types/strapi/strapi'
import { calcPrinterMonthlyStats } from '../helpers/calc-printer-monthly-stats'

export interface PrinterPrintJobRow {
    key: string
    clientName: string
    shipmentId: number | string
    start: string
    end?: string
}

function buildQueryParams(userDocumentId: string, month: Dayjs): EndpointQueryParameters {
    const monthStart = month.startOf('month').toISOString()
    const monthEnd = month.endOf('month').toISOString()

    return {
        filters: {
            user: { documentId: { $eq: userDocumentId } },
            start: { $gte: monthStart, $lte: monthEnd },
            reason: { $eq: Reasons.Done },
        },
        populate: {
            machine: {
                populate: {
                    machine_brand: true,
                },
            },
            print_job: {
                populate: {
                    material_consumptions: {
                        populate: {
                            material: true,
                        },
                    },
                    shipment: {
                        populate: {
                            client: true,
                            calculated: true,
                            calculatedOnActual: true,
                        },
                    },
                },
            },
        },
        sort: ['start:desc'],
    }
}

function mapExecutorToRow(executor: PrintJobExecutor): PrinterPrintJobRow {
    const shipment = executor.print_job?.shipment

    return {
        key: executor.documentId,
        clientName: shipment?.client?.name ?? '—',
        shipmentId: shipment?.id ?? '—',
        start: executor.start,
        end: executor.end,
    }
}

export default function usePrinterPrintJobs(month: Dayjs) {
    const user = useUser()

    const queryParams = useMemo(
        () => (user?.documentId ? buildQueryParams(user.documentId, month) : undefined),
        [user?.documentId, month]
    )

    const { data, isLoading, isFetching } = useGetPrintJobExecutorsAllPagesQuery(
        queryParams as EndpointQueryParameters,
        { skip: !queryParams }
    )

    const executors = data ?? []

    const rows = useMemo(() => executors.map(mapExecutorToRow), [executors])
    const stats = useMemo(() => calcPrinterMonthlyStats(executors), [executors])

    return {
        rows,
        isLoading: isLoading || isFetching,
        stats,
    }
}
